import { DialogConfirm } from '../elements/DialogConfirm';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Stack } from '@mui/material';
import React, { useEffect } from 'react';
import { InputText } from '../elements/InputText';
import { ButtonCustom, ButtonOutLine } from '../elements/ButtonCustom';
import { FormGenReportSchema } from 'utils/validate';
import { CustomRadioGroup } from '../elements/RadioCustom';
import { InputSelectMultipleInfinite } from '../elements/InputSelectMultiInfinite';
import { Documentation } from 'types/documents';
import { InputSelectMultiple } from '../elements/InputSelectMultiple';
import { Checkbox } from '../elements/CheckBox';
import dayjs from 'dayjs';
import { DatePicker } from '../elements/DatePicker';
export type FormGenerateReport = {
  title: string;
  assets?: string[];
  issues?: string[];
  roomLevelIssueIds?: string[];
  select?: string;
  storageIds?: string[];
  showAppendixFile?: boolean;
  date?: string;
};
type ModalGenerateReportProps = {
  isOpen: boolean;
  onCloseModal: () => void;
  onCancel: () => void;
  onSubmit: (value: FormGenerateReport) => void;
  optionAsset: { value: string; label: string }[];
  optionIssues: { value: string; label: string }[];
  optionRoomLevelIssues: { value: string; label: string }[];
  handleFetchMoreIssues: () => void;
  hasMoreIssues: boolean;
  documentGenerate: Documentation[];
};
export const ModalGenerateReport = ({
  isOpen,
  onCloseModal,
  onCancel,
  onSubmit,
  optionAsset,
  optionIssues,
  handleFetchMoreIssues,
  hasMoreIssues,
  optionRoomLevelIssues,
  documentGenerate
}: ModalGenerateReportProps) => {
  const {
    handleSubmit,
    register,
    control,
    setValue,
    reset,
    watch,
    formState: { errors }
  } = useForm<FormGenerateReport>({
    resolver: yupResolver(FormGenReportSchema),
    defaultValues: { select: 'selective' }
  });
  useEffect(() => {
    if (isOpen) {
      reset();
    }
  }, [isOpen, reset]);
  return (
    <DialogConfirm
      maxWidth={700}
      textTitle={'Generate Report'}
      isOpen={isOpen}
      onClose={onCloseModal}>
      <Stack width={'100%'} gap={2.5}>
        <Stack width={'100%'} gap={2} alignItems={'flex-start'} justifyContent={'space-between'}>
          <InputText
            inputError={errors.title?.message}
            placeholder={'Enter report title'}
            inputProps={{
              ...register('title'),
              style: {
                minHeight: '46px',
                height: '46px'
              }
            }}
          />
          <Controller
            control={control}
            render={({ field: { onChange, value } }) => (
              <Checkbox value={!!value} onCheck={onChange} label={'Show Appendix File'} />
            )}
            name={`showAppendixFile`}
          />
          <Stack
            width={'100%'}
            direction="row"
            alignItems="center"
            justifyContent={'space-between'}>
            <Controller
              control={control}
              render={({ field: { onChange, value } }) => (
                <CustomRadioGroup
                  value={value || 'selective'}
                  onChange={onChange}
                  options={[
                    { value: 'selective', label: 'Selective Assets' },
                    { value: 'selectIssues', label: 'Selective Issues' },
                    { value: 'all', label: 'All' }
                  ]}
                />
              )}
              name={`select`}
            />
            <Stack width="min-content" minWidth={220}>
              <Controller
                control={control}
                render={({ field: { onChange, value } }) => (
                  <DatePicker
                    title="As Of Date"
                    value={value ? dayjs(value) : null}
                    style={{ flexDirection: 'column', width: '100%', gap: '10px' }}
                    titleStyle={{ color: '#226AAA', fontWeight: '700' }}
                    onChange={(val) => val && onChange(val?.toISOString())}
                  />
                )}
                name={`date`}
              />
            </Stack>
          </Stack>
          {watch('select') === 'selective' && (
            <Stack style={{ width: '100%', minHeight: '56px' }}>
              <Controller
                control={control}
                render={({ field: { value } }) => (
                  <InputSelectMultipleInfinite
                    onChangeInput={(selectedValues) => {
                      setValue('assets', selectedValues);
                    }}
                    handleFetchMore={() => {}}
                    hasMore={false}
                    hasSelectAll={true}
                    selectAllLabel={'Select All'}
                    inputError={errors.assets?.message as string}
                    listChooseSelect={value || []}
                    placeholder={'Select Assets'}
                    sx={{ padding: 0 }}
                    options={optionAsset || []}
                  />
                )}
                name={'assets'}
              />
            </Stack>
          )}
          {watch('select') === 'selectIssues' && (
            <Stack style={{ width: '100%', minHeight: '56px' }}>
              <Controller
                control={control}
                render={({ field: { value } }) => (
                  <InputSelectMultipleInfinite
                    onChangeInput={(selectedValues) => {
                      setValue('issues', selectedValues);
                    }}
                    handleFetchMore={handleFetchMoreIssues}
                    hasMore={hasMoreIssues}
                    hasSelectAll={true}
                    selectAllLabel={'Select All'}
                    inputError={errors.issues?.message as string}
                    listChooseSelect={value || []}
                    placeholder={'Select Issues'}
                    sx={{ padding: 0 }}
                    options={optionIssues || []}
                  />
                )}
                name={'issues'}
              />
            </Stack>
          )}
          <Stack style={{ width: '100%', minHeight: '56px' }}>
            <Controller
              control={control}
              render={({ field: { value } }) => (
                <InputSelectMultipleInfinite
                  label={'Select Location Level Issues'}
                  handleFetchMore={() => {}}
                  hasSelectAll={true}
                  selectAllLabel={'Select All'}
                  onChangeInput={(selectedValues) => {
                    setValue('roomLevelIssueIds', selectedValues);
                  }}
                  inputError={errors.roomLevelIssueIds?.message as string}
                  listChooseSelect={value || []}
                  placeholder={'Select Location Level Issues'}
                  sx={{ padding: 0 }}
                  options={optionRoomLevelIssues || []}
                />
              )}
              name={'roomLevelIssueIds'}
            />
            <Controller
              render={({ field }) => (
                <InputSelectMultiple
                  inputError={errors.storageIds?.message as string}
                  placeholder={'Select Documentations'}
                  label={'Select Documentations'}
                  hasSelectAll={true}
                  selectAllLabel={'Select All'}
                  options={documentGenerate?.map((item) => ({
                    value: item.storageId.toString(),
                    label: item?.storage?.fileName
                  }))}
                  listChooseSelect={watch('storageIds') || []}
                  onChangeInput={(selectedValues) => {
                    setValue('storageIds', selectedValues);
                  }}
                  inputProps={{
                    ...field,
                    defaultValue: [],
                    style: { width: '100%' }
                  }}
                />
              )}
              name="storageIds"
              control={control}
            />
          </Stack>
        </Stack>
        <Stack
          flexDirection={'row'}
          alignItems={'flex-end'}
          justifyContent={'space-between'}
          width={'100%'}>
          <Stack />
          <Stack flexDirection={'row'} gap={2.5}>
            <ButtonOutLine label={'Cancel'} style={{ width: 166 }} onClick={onCancel} fullWidth />
            <ButtonCustom
              style={{ width: 166 }}
              onClick={handleSubmit(onSubmit)}
              label={'Generate Report'}
            />
          </Stack>
        </Stack>
      </Stack>
    </DialogConfirm>
  );
};
